/**
 * Pricing derivations the FRONTEND owns for live feedback while a purchaser edits a
 * quote line (contract §3):
 *   - category-margin default sell price
 *   - pricing-rule violations shown as red flags as they type
 *   - commission / convenience recompute on the totals
 *
 * Task 11 (`quoflow.quoflow.quoflow.api.quotation.*`) changed WHO is authoritative, and this
 * file's own role along with it: there is no standalone `validate` endpoint any more — the FE
 * calculates margins and totals, and the backend stores what it is sent (this task's binding
 * rule), so `previewTotals` below is not a preview of a server number that will supersede it; it
 * IS the number, reused verbatim by `useQuotation.ts`'s `toQuotation` to build the persisted view
 * too. Corrected (fix round, final review: I7) — that mapper's own header used to say the same
 * thing this one did, and both were wrong the same way: task 1 made `toQuotation` TRUST `markup`
 * off the wire (a RATE, echoed configuration — see `useQuotation.ts`'s header) while it still
 * distrusts `grand_total` (a STALE total, since `update_quotation_line` writes one line, never
 * the header). `useQuotation.ts`'s header was fixed already; this one, asserting the opposite of
 * what the file actually does, was not — until now. What remains genuinely the SERVER's:
 * `quotation.send_quotation`'s `violations[]` is the authoritative enforcement gate on send
 * (`checkLine`/`checkLines` below only mirror it for live feedback — a mismatch between the two
 * is a bug in this file, not in the server), and whatever a non-FE reader (a PDF, the old
 * `/api/v1` façade) sees as the persisted total is the backend's own bookkeeping on its stored
 * lines, not a number this file writes.
 *
 * STUB: enough for the quote screen to be built against; extend the rule kinds here
 * (not in the screen) as `docs/bosun-api-contract.md` §10 grows.
 */

import type {
  CategoryMargins,
  ItemCategory,
  Money,
  PricingRule,
  PricingRulesSettings,
  QuotationLine,
  QuotationTotals,
  RuleViolation,
} from './types'

/**
 * Sell price from a cost and a margin percentage. The one place that arithmetic lives — callers
 * that resolve their own margin (see `quotationFromAllocation.ts`, which falls back to
 * `default_markup_pct` for the usual category-less line) go through this rather than repeating it.
 */
export function sellFromMargin(cost: Money, pct: number): Money {
  return { amount: cost.amount * (1 + pct / 100), currency: cost.currency }
}

/** Default sell unit price from the category margin, before any manual override. */
export function defaultSellUnit(cost: Money, category: ItemCategory, margins: CategoryMargins): Money {
  return sellFromMargin(cost, margins[category] ?? 0)
}

/**
 * Markup as a percentage of cost.
 *
 * A zero cost yields 0%, not infinity — matching the prototype (`ruleViolate`, line 926).
 * The effect is that a line with no known cost trips any minimum-markup rule and is flagged
 * for a human to look at, which is the safe direction: an unpriced cost is a data problem,
 * not a licence to sell at any margin.
 */
export function markupPct(cost: Money, sell: Money): number {
  if (cost.amount <= 0) return 0
  return ((sell.amount - cost.amount) / cost.amount) * 100
}

/**
 * Live rule feedback for one line. Mirrors what the server checks on validate/send;
 * a mismatch between the two is a bug in this file, not in the screen.
 */
export function checkLine(line: Pick<QuotationLine, 'line_id' | 'category' | 'cost_unit' | 'sell_unit'>, settings: PricingRulesSettings): RuleViolation[] {
  const violations: RuleViolation[] = []
  const mk = markupPct(line.cost_unit, line.sell_unit)

  for (const rule of settings.rules) {
    switch (rule.kind) {
      case 'noBelowCost':
        if (line.sell_unit.amount < line.cost_unit.amount) {
          violations.push({ line_id: line.line_id, reason: 'below cost' })
        }
        break
      case 'minMarkup':
        if (mk < rule.pct) {
          violations.push({ line_id: line.line_id, reason: `markup ${mk.toFixed(1)}% < ${rule.pct}%` })
        }
        break
      case 'minMarkupCat':
        if (rule.cat === line.category && mk < rule.pct) {
          violations.push({
            line_id: line.line_id,
            reason: `${rule.cat} markup ${mk.toFixed(1)}% < ${rule.pct}%`,
          })
        }
        break
      case 'roundSell':
        // Tolerance on the ratio, as the prototype does (line 930): comparing the amounts
        // directly would flag a price that is off by a rounding artefact rather than a step.
        if (rule.step > 0 && Math.abs(line.sell_unit.amount / rule.step - Math.round(line.sell_unit.amount / rule.step)) > 1e-6) {
          violations.push({ line_id: line.line_id, reason: `not rounded to ${rule.step}` })
        }
        break
    }
  }
  return violations
}

/**
 * Human-readable rule text. Used by the Settings screen to describe each configured rule;
 * the quote screen shows the per-line violation reason instead, which is more specific.
 */
export function ruleText(rule: PricingRule): string {
  switch (rule.kind) {
    case 'noBelowCost':
      return 'Selling price must not be below cost'
    case 'minMarkup':
      return `Minimum markup ${rule.pct}% (all items)`
    case 'minMarkupCat':
      return `Minimum markup ${rule.pct}% for category “${rule.cat}”`
    case 'roundSell':
      return `Round selling to nearest ${rule.step}`
  }
}

export function checkLines(lines: QuotationLine[], settings: PricingRulesSettings): RuleViolation[] {
  return lines.flatMap((l) => checkLine(l, settings))
}

/** A queued structural edit, as `QuotationEditor`'s `StructuralEditing.pending` map holds it. */
export interface PendingLineEdit {
  op: 'set_qty' | 'remove' | 'add'
  qty: number
}

/**
 * `lines`, minus anything queued for removal, with `qty` overridden to the queued value where one
 * is pending — CLAUDE.md's "shared derivations go in lib/" rule, applied after a fix-round finding
 * (final review: I6) found this logic duplicated between `QuotationEditor`'s totals preview and
 * `QuoteLinesTable`'s group subtotal, computed at slightly different times from slightly different
 * inputs. The result: resizing a queued qty 3→10 on a 100/unit line showed the qty box at 10, the
 * row's OWN "Line" figure still at 300 (`QuoteRow` multiplied `sell_unit` by the SAVED `line.qty`,
 * not the queued one), the group subtotal at 300 too, and the totals panel — the one place already
 * routing through this derivation — correctly at 1000. Four numbers, three of them wrong, for one
 * pending edit. One function now decides what "the quantity that counts" is for every SUM on the
 * screen; a row's own on-screen presentation (the strike-through, the "pending" badge) is a
 * separate concern and untouched by this filter, because a queued removal must stay visible until
 * the batch is actually applied — only what gets SUMMED treats it as already gone.
 */
export function effectiveLines<T extends { item_code: string; qty: number }>(
  lines: T[],
  pending: Map<string, PendingLineEdit> | undefined,
): T[] {
  if (!pending || pending.size === 0) return lines
  return lines
    .filter((l) => pending.get(l.item_code)?.op !== 'remove')
    .map((l) => {
      const edit = pending.get(l.item_code)
      return edit?.op === 'set_qty' ? { ...l, qty: edit.qty } : l
    })
}

/**
 * The quotation's totals, computed from its lines. Corrected (fix round, final review: I7): this
 * used to say `POST /quotations/{id}` and the send step "return the persisted totals, which
 * always win" — that route does not exist on this transport, and per this file's own header there
 * is no separate persisted number that supersedes what this function returns. `useQuotation.ts`'s
 * `toQuotation` calls this SAME function on the current lines to build the view a screen shows;
 * the only thing that can still disagree with it is the wire's own `grand_total`, which is
 * deliberately never read for exactly that reason.
 */
export function previewTotals(
  lines: QuotationLine[],
  opts: { commission_pct: number; convenience: Money; markup_pct: number },
): QuotationTotals {
  const currency = opts.convenience.currency
  const costBase = lines.reduce((s, l) => s + l.qty * l.cost_unit.amount, 0)
  const sellSubtotal = lines.reduce((s, l) => s + l.qty * l.sell_unit.amount, 0)

  /*
    The total is the SUM OF THE LINES. Nothing is added on top.

    The business rule (Akhil, 2026-09-04): "markup and commission are for line items only, not
    total bill, based on the formula". Each line already sells at
    `((cost x markup) + cost) / ((100 - commission) / 100)` — see `quotationCommission.ts`'s
    `applyMarkup` — so adding either again here bills the customer for it twice. That is the
    defect reported against AED 800.00 of cost being quoted at 1,173.30 instead of 960.00.

    This used to mirror the SERVER's formula instead:

        grand_total = net x (1 + markup/100) + convenience + (net x commission/100)

    measured on QTN-2026-00004 (2026-08-09) and re-measured on QTN-2026-00014 (2026-09-04:
    net 868.66, markup 30 -> 260.60, commission 40 -> 347.46, grand 1,476.72). The server still
    computes it, and it contradicts the rule — filed as `D27`. It stays harmless only because this
    app never writes `markup`, the four commission fields or `convenience_amount` as anything but
    0, which makes the server's `grand_total` equal the line subtotal. If one of those is ever
    set non-zero by the ERP directly, the stored total will exceed what this shows.
  */
  const total = sellSubtotal

  return {
    cost_base: { amount: costBase, currency },
    sell_subtotal: { amount: sellSubtotal, currency },
    commission_pct: opts.commission_pct,
    markup_pct: opts.markup_pct,
    convenience: opts.convenience,
    total: { amount: total, currency },
    // Over COST, matching contract §9's worked example and the prototype (line 998).
    margin_pct: costBase > 0 ? ((sellSubtotal - costBase) / costBase) * 100 : 0,
  }
}
